/**
 * Composants de saisie partages entre :
 *   - QualificationDetailPage (admin)
 *   - PreQualificationPage (formulaire public client)
 *
 * Avant : ils etaient definis localement dans QualificationDetailPage,
 * ce qui rendait PreQualificationPage cassee (RentalsList is not defined).
 */
import React from 'react';

// ============================================================
// FField - champ de formulaire universel utilise par tous
// ============================================================
export const FField = ({ label, type = 'text', value, onChange, opts = null, placeholder = '' }) => {
  const baseCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#008A8D] focus:outline-none focus:ring-1 focus:ring-[#008A8D] bg-white';
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">{label}</span>
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={baseCls}>
          {(opts || []).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`${baseCls} min-h-[80px]`} />
      ) : (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseCls} />
      )}
    </label>
  );
};

// ============================================================
// CreditsList - liste de credits en cours
// ============================================================
export const CreditsList = ({ credits, onChange }) => {
  const list = credits.length > 0 ? credits : [{ label: '', monthly: '', remaining: '', bank: '' }];
  const addCredit = () => onChange([...list, { label: '', monthly: '', remaining: '', bank: '' }]);
  const removeCredit = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateCredit = (i, k, v) => { const c = [...list]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const totalMonthly = list.reduce((s, c) => s + (parseFloat(c.monthly) || 0), 0);
  return (
    <div>
      {list.map((c, i) => (
        <div key={i} className="relative border border-gray-100 rounded-lg p-2.5 pr-8 mb-2 bg-gray-50/40">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div><label className="text-xs text-gray-500">Intitule</label><input type="text" value={c.label || ''} onChange={e => updateCredit(i, 'label', e.target.value)} className="realease-input text-sm" placeholder="Pret immo, auto, conso..." /></div>
            <div><label className="text-xs text-gray-500">Banque</label><input type="text" value={c.bank || ''} onChange={e => updateCredit(i, 'bank', e.target.value)} className="realease-input text-sm" placeholder="Credit Agricole, BNP..." data-testid={`credit-bank-${i}`} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-gray-500">Mensualite (EUR)</label><input type="number" value={c.monthly || ''} onChange={e => updateCredit(i, 'monthly', e.target.value)} className="realease-input text-sm text-right" placeholder="0" /></div>
            <div><label className="text-xs text-gray-500">Capital restant du (EUR)</label><input type="number" value={c.remaining || ''} onChange={e => updateCredit(i, 'remaining', e.target.value)} className="realease-input text-sm text-right" placeholder="0" /></div>
          </div>
          {list.length > 1 && (
            <button onClick={() => removeCredit(i)} className="absolute top-1.5 right-1.5 text-red-500 text-lg leading-none hover:bg-red-50 rounded px-1" title="Retirer">&times;</button>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between mt-2">
        <button onClick={addCredit} className="text-[#008A8D] text-sm font-medium hover:underline">+ Ajouter un credit</button>
        {totalMonthly > 0 && <span className="text-sm font-medium text-gray-700">Total mensualites : {totalMonthly.toLocaleString('fr-FR')} EUR</span>}
      </div>
    </div>
  );
};

// ============================================================
// ChargesList - liste des charges mensuelles
// ============================================================
export const ChargesList = ({ charges, onChange }) => {
  const list = charges.length > 0 ? charges : [{ label: 'Pension alimentaire', amount: '' }];
  const addCharge = () => onChange([...list, { label: '', amount: '' }]);
  const removeCharge = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateCharge = (i, k, v) => { const c = [...list]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const presets = ['Loyer', 'Pension alimentaire', 'Assurance', 'Impots', 'Autre'];
  const total = list.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  return (
    <div className="mb-4">
      {list.map((c, i) => (
        <div key={i} className="grid grid-cols-3 gap-2 mb-2 items-end">
          <div><label className="text-xs text-gray-500">Type</label>
            <select value={presets.includes(c.label) ? c.label : 'custom'} onChange={e => updateCharge(i, 'label', e.target.value === 'custom' ? '' : e.target.value)} className="realease-input text-sm">
              {presets.map(p => <option key={p} value={p}>{p}</option>)}
              <option value="custom">Autre (saisir)</option>
            </select>
            {!presets.includes(c.label) && <input type="text" value={c.label || ''} onChange={e => updateCharge(i, 'label', e.target.value)} className="realease-input text-sm mt-1" placeholder="Preciser..." />}
          </div>
          <div><label className="text-xs text-gray-500">Montant mensuel (EUR)</label><input type="number" value={c.amount || ''} onChange={e => updateCharge(i, 'amount', e.target.value)} className="realease-input text-sm" /></div>
          {list.length > 1 ? (
            <button onClick={() => removeCharge(i)} className="text-red-500 text-sm hover:underline pb-2">Retirer</button>
          ) : <span></span>}
        </div>
      ))}
      <div className="flex items-center justify-between mt-2">
        <button onClick={addCharge} className="text-[#008A8D] text-sm font-medium hover:underline">+ Ajouter une charge</button>
        {total > 0 && <span className="text-sm font-medium text-gray-700">Total charges : {total.toLocaleString('fr-FR')} EUR</span>}
      </div>
    </div>
  );
};

// ============================================================
// RentalsList - liste des loyers percus
// ============================================================
export const RentalsList = ({ rentals, onChange }) => {
  const list = rentals.length > 0 ? rentals : [{ label: '', rent: '', address: '' }];
  const add = () => onChange([...list, { label: '', rent: '', address: '' }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const update = (i, k, v) => { const r = [...list]; r[i] = { ...r[i], [k]: v }; onChange(r); };
  const total = list.reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  return (
    <div className="mb-4">
      {list.map((r, i) => (
        <div key={i} className="grid grid-cols-[1.5fr_2fr_1fr_auto] gap-2 mb-2 items-end">
          <div><label className="text-xs text-gray-500">Intitule du bien</label><input type="text" value={r.label || ''} onChange={e => update(i, 'label', e.target.value)} className="realease-input text-sm" placeholder="Appart Paris 15e..." /></div>
          <div><label className="text-xs text-gray-500">Adresse / Reference</label><input type="text" value={r.address || ''} onChange={e => update(i, 'address', e.target.value)} className="realease-input text-sm" placeholder="12 rue..." /></div>
          <div><label className="text-xs text-gray-500">Loyer mensuel (EUR)</label><input type="number" value={r.rent || ''} onChange={e => update(i, 'rent', e.target.value)} className="realease-input text-sm" /></div>
          {list.length > 1 ? (
            <button onClick={() => remove(i)} className="text-red-500 text-sm hover:underline pb-2">Retirer</button>
          ) : <span></span>}
        </div>
      ))}
      <div className="flex items-center justify-between mt-2">
        <button onClick={add} className="text-[#008A8D] text-sm font-medium hover:underline">+ Ajouter un loyer percu</button>
        {total > 0 && <span className="text-sm font-medium text-gray-700">Total loyers : {total.toLocaleString('fr-FR')} EUR/mois</span>}
      </div>
    </div>
  );
};

// ============================================================
// ExtraAssociates - associes / investisseurs supplementaires
// ============================================================
export const ExtraAssociates = ({ list, onChange }) => {
  const add = () => onChange([...list, { name: '', relation: '', entity_type: 'personne_physique', apport: '', income: '', stability: '', rentals_list: [], credits_list: [], charges_list: [] }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => { const a = [...list]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  return (
    <div className="space-y-4">
      {list.length === 0 && <p className="text-sm text-gray-400 italic">Aucun investisseur supplementaire.</p>}
      {list.map((a, i) => (
        <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-4 border border-gray-200" data-testid={`extra-assoc-${i}`}>
          <div className="flex justify-between items-center">
            <h5 className="font-semibold text-gray-700">Investisseur #{i + 3}{a.entity_type === 'sci' ? ' — SCI' : ''}</h5>
            <button onClick={() => remove(i)} className="text-red-500 text-sm hover:underline">Retirer</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FField label="Type d'investisseur" type="select" value={a.entity_type || 'personne_physique'} onChange={v => upd(i, 'entity_type', v)}
                    opts={[{v:'personne_physique',l:'Personne physique'},{v:'sci',l:'SCI (personne morale)'}]} />
            <FField label={a.entity_type === 'sci' ? 'Denomination de la SCI' : 'Nom'} type="text" value={a.name} onChange={v => upd(i, 'name', v)} />
            <FField label="Lien / role" type="text" value={a.relation} onChange={v => upd(i, 'relation', v)} />
            <FField label="Apport pour ce projet (EUR)" type="number" value={a.apport} onChange={v => upd(i, 'apport', parseFloat(v) || null)} />
            <FField label={a.entity_type === 'sci' ? 'Resultat net mensuel de la SCI (EUR)' : 'Revenu net fiscal mensuel (EUR)'} type="number" value={a.income} onChange={v => upd(i, 'income', parseFloat(v) || null)} />
            <FField label="Stabilite des revenus" type="select" value={a.stability} onChange={v => upd(i, 'stability', v)}
                    opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
          </div>
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">Loyers percus</h6>
            <RentalsList rentals={a.rentals_list || []} onChange={v => upd(i, 'rentals_list', v)} />
          </div>
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">Credits en cours</h6>
            <CreditsList credits={a.credits_list || []} onChange={v => upd(i, 'credits_list', v)} />
          </div>
          <div>
            <h6 className="text-sm font-medium text-gray-600 mb-2">Charges mensuelles</h6>
            <ChargesList charges={a.charges_list || []} onChange={v => upd(i, 'charges_list', v)} />
          </div>
        </div>
      ))}
      <button onClick={add} className="text-[#008A8D] text-sm font-medium hover:underline" data-testid="add-extra-associate">+ Ajouter un investisseur</button>
    </div>
  );
};
